"use client";

import { Calendar, RefreshCw } from "lucide-react";
import { DateRangePicker } from "../ui/date-picker";
import { useState } from "react";

interface PageHeaderProps {
  title: string;
  description?: string;
  tabs?: {
    id: string;
    label: string;
    disabled?: boolean;
  }[];
  defaultTab?: string;
  onTabChange?: (tabId: string) => void;
  onRefresh?: () => void;
  onDateClick?: () => void;
  showDateAndRefresh?: boolean;
  onDateRangeChange?: (range: { from: Date; to?: Date } | undefined) => void;
  selectedDateRange?: { from: Date; to?: Date };
  showDatePicker?: boolean;
}

export function PageHeader({
  title,
  description,
  tabs,
  defaultTab,
  onTabChange,
  onRefresh,
  onDateClick,
  showDateAndRefresh = false,
  onDateRangeChange,
  selectedDateRange,
  showDatePicker = false
}: PageHeaderProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || (tabs && tabs.length > 0 ? tabs[0].id : ''));

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  // Get current date in format: May 21, 2025
  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="mb-6 md:mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          {description && (
            <p className="text-gray-500 dark:text-gray-400">{description}</p>
          )}
        </div>
        {(showDateAndRefresh || showDatePicker) && (
          <div className="flex items-center gap-2">
            {showDatePicker && onDateRangeChange ? (
              <DateRangePicker
                selected={selectedDateRange}
                onSelect={(range) => {
                  if (range && typeof range === 'object' && 'from' in range) {
                    onDateRangeChange(range);
                  }
                }}
                placeholder="Select date range"
                className="w-64"
              />
            ) : showDateAndRefresh && (
              <div className="relative">
                <button
                  onClick={onDateClick}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 transform active:scale-95"
                >
                  <Calendar className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span>{currentDate}</span>
                </button>
              </div>
            )}

            {(showDateAndRefresh || showDatePicker) && onRefresh && (
              <button
                onClick={onRefresh}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 transform active:scale-95"
              >
                <RefreshCw className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
            )}
          </div>
        )}
      </div>

      {tabs && tabs.length > 0 && (
        <div className="flex items-center gap-1 mt-6 border-b border-gray-200 dark:border-gray-800">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              disabled={tab.disabled}
              className={`px-4 py-2 text-sm font-medium transition-all duration-150 border-b-2 ${
                tab.disabled
                  ? 'border-transparent text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : activeTab === tab.id
                    ? 'border-gray-700 dark:border-gray-300 text-gray-900 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)' }}
              onClick={() => !tab.disabled && handleTabChange(tab.id)}
            >
              {tab.disabled ? (
                <span className="flex items-center gap-2">
                  {tab.label}
                  <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">Coming Soon</span>
                </span>
              ) : (
                tab.label
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}